import { motion } from "framer-motion";
import { 
  DollarSign, 
  Users, 
  BookOpen, 
  TrendingUp,
  CreditCard,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

function DashboardMockup() {
  const stats = [
    { label: "Total Revenue", value: "$12,345", change: "+12.5%", up: true, icon: DollarSign },
    { label: "Active Students", value: "1,234", change: "+8.2%", up: true, icon: Users },
    { label: "Products", value: "12", change: "+2", up: true, icon: BookOpen },
    { label: "Conversion", value: "4.8%", change: "-0.3%", up: false, icon: TrendingUp },
  ];

  const recentSales = [
    { name: "Sarah J.", product: "Complete Course", amount: "$99", time: "2m ago" },
    { name: "Mike C.", product: "Template Pack", amount: "$29", time: "15m ago" },
    { name: "Emma W.", product: "1:1 Coaching", amount: "$149", time: "1h ago" },
    { name: "John D.", product: "Complete Course", amount: "$99", time: "2h ago" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true }}
      className="bg-neutral-900 rounded-[20px] overflow-hidden border border-neutral-800"
    >
      {/* Header */}
      <div className="h-10 border-b border-neutral-800 flex items-center px-4 gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="h-6 px-4 rounded bg-neutral-800 flex items-center text-xs text-neutral-400">
            uselint.com
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Welcome */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white">Welcome back, Creator 👋</h3>
          <p className="text-sm text-neutral-400">Here's how your products are performing today.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              className="bg-neutral-800 rounded-xl p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <stat.icon className="w-5 h-5 text-emerald-500" />
                <span className={`text-xs flex items-center gap-0.5 ${stat.up ? 'text-emerald-400' : 'text-red-400'}`}>
                  {stat.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {stat.change}
                </span>
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-neutral-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Chart + Recent Sales */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Revenue Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            viewport={{ once: true }}
            className="bg-neutral-800 rounded-xl p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-white">Revenue Overview</h4>
              <BarChart3 className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="flex items-end justify-between h-32 gap-2">
              {[
                { total: 40, filled: 65 },
                { total: 65, filled: 70 },
                { total: 45, filled: 55 },
                { total: 80, filled: 60 },
                { total: 55, filled: 75 },
                { total: 90, filled: 50 },
              ].map((bar, i) => (
                <div
                  key={i}
                  className="flex-1 bg-emerald-500/20 rounded-t"
                  style={{ height: `${bar.total}%` }}
                >
                  <div 
                    className="w-full bg-emerald-500 rounded-t"
                    style={{ height: `${bar.filled}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-neutral-500">
              <span>Jan</span>
              <span>Feb</span>
              <span>Mar</span>
              <span>Apr</span>
              <span>May</span>
              <span>Jun</span>
            </div>
          </motion.div>

          {/* Recent Sales */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            viewport={{ once: true }}
            className="bg-neutral-800 rounded-xl p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-white">Recent Sales</h4>
              <CreditCard className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="space-y-3">
              {recentSales.map((sale, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center text-xs font-medium text-neutral-300">
                    {sale.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{sale.name}</div>
                    <div className="text-[10px] text-neutral-400 truncate">{sale.product}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-emerald-400">{sale.amount}</div>
                    <div className="text-[10px] text-neutral-500">{sale.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

const features = [
  {
    icon: TrendingUp,
    title: "Real-time Analytics",
    description: "Track views, enrollments, and revenue as they happen",
  },
  {
    icon: Users,
    title: "Student Management",
    description: "See who's enrolled, their progress, and engagement",
  },
  {
    icon: DollarSign,
    title: "Earnings Tracking",
    description: "Monitor revenue, fees, and upcoming payouts",
  },
  {
    icon: CreditCard,
    title: "Payout Management",
    description: "Connect Stripe and get paid on your schedule",
  },
];

export function DashboardShowcase() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-6">
              Everything You Need.{" "}
              <span className="text-emerald-500">One Dashboard.</span>
            </SectionHeading>
            
            <SectionSubheading className="mb-8">
              Track your earnings, manage students, view analytics, and handle payouts — 
              all in one clean, powerful dashboard.
            </SectionSubheading>

            <div className="space-y-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Dashboard Mockup */}
          <div className="relative rounded-[32px] border border-neutral-200/50 dark:border-neutral-700/50 bg-neutral-100/80 dark:bg-neutral-800/50 p-2 md:p-4">
            <div className="relative rounded-[24px] overflow-hidden">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

